import { STORM_KIND, type WeatherKind } from "./weather.ts";
import type { WindHeading, WindStrength } from "./wind.ts";

/**
 * The wind presets of the game's effects library (ADR-144's amendment of 2026-10-31), beside the rain presets: thin wisps that
 * draw themselves across the screen (`wind-wisp`), gust dashes (`wind-dash`) and the shadows of passing clouds
 * (`cloud-shadow`). They are sprite presets, not particle emitters: each instance is one sprite whose place and frame are a
 * pure function of the clock (`wind-schedule.ts`). Every number a look rests on is a named value here.
 */

/**
 * The peak opacity of a wisp's line in a breeze, wind and storm. The sheet's own line is 20 percent opaque; the cut lifts it to
 * full (`WIND_SHEETS`), so this is what a line peaks at.
 */
export const WISP_OPACITY = { breeze: 0.33, windy: 0.37, storm: 0.43 } as const;

/**
 * How far from each end of a wisp's line it fades, in art pixels: the mockup's 38 screen pixels at the world's zoom of 2. The
 * cut tapers each line's ends by it.
 */
export const WISP_END_FADE_PX = 19;

/**
 * The peak opacity of a gust dash in a breeze, wind and storm.
 */
export const DASH_OPACITY = { breeze: 0.36, windy: 0.36, storm: 0.45 } as const;

/**
 * How much a cloud's shadow darkens what it falls on, and its colour.
 */
export const CLOUD_SHADOW_OPACITY = 0.2;
export const CLOUD_SHADOW_TINT = 0x141e2e;

/**
 * How many degrees from straight down a storm's wind slants the rain.
 */
export const STORM_RAIN_SLANT = 28;

/**
 * Where wisps and dashes run, as shares of the view's height: nearly the whole height, top to bottom. The HUD's panels are drawn over the game, so a wisp that crosses one passes behind it.
 */
export const WIND_LANE: readonly [number, number] = [0.05, 0.95];

/**
 * How long a wisp or dash schedule takes to repeat, in milliseconds: 144 ticks of 80 ms, the mockups' clip.
 */
export const WIND_PERIOD_MS = 11_520;

/**
 * How long a wisp or dash takes to fade out at the end of a turn and in at the start of the next, in milliseconds: the wind's
 * count, hold and drift change at a turn's edge, so none is drawn at full strength across it.
 */
export const WIND_EDGE_FADE_MS = 5000;

/**
 * The cloud field repeats every this many world pixels across and down, so the same clouds drift over any map.
 */
export const CLOUD_TILE = { width: 4096, height: 2048 } as const;

/**
 * How many clouds a tile holds in fair weather and in a cloudier clear turn.
 */
export const FAIR_CLOUDS = 4;
export const CLOUDY_CLOUDS = 45;

/**
 * How long a cloud takes to fade in at the start of a turn and out at its end, in milliseconds.
 */
export const CLOUD_FADE_MS = 30_000;

/**
 * The most cloud shadows one view can show at once.
 */
export const MAX_CLOUD_SLOTS = 24;

/**
 * One sheet of wind art as the cut writes it and the page loads it: a grid of `columns` cells, row by row, of `frameWidth` by
 * `frameHeight`, holding `frames` frames.
 */
export interface WindSheet {
  readonly id: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly columns: number;
  readonly frames: number;
}

/**
 * The sheets of the wind art, by id. The wisp sheets are 7 by 7 grids of the packs' 48 frame strips; the dash sheets are the
 * Highlands grids as drawn; the cloud sheet is the five silhouettes scaled 2 times and softened, each in a cell padded by
 * `CLOUD_SHEET_PAD` on every side.
 */
export const CLOUD_SHEET_PAD = 24;
export const WIND_SHEETS: readonly WindSheet[] = [
  { id: "wisp-sea", frameWidth: 288, frameHeight: 64, columns: 7, frames: 48 },
  { id: "wisp-village", frameWidth: 312, frameHeight: 64, columns: 7, frames: 48 },
  { id: "dash-1", frameWidth: 224, frameHeight: 96, columns: 4, frames: 14 },
  { id: "dash-2", frameWidth: 224, frameHeight: 96, columns: 4, frames: 15 },
  { id: "dash-3", frameWidth: 224, frameHeight: 96, columns: 4, frames: 10 },
  { id: "dash-4", frameWidth: 224, frameHeight: 96, columns: 4, frames: 16 },
  { id: "cloud-shadows", frameWidth: 224 * 2 + 2 * CLOUD_SHEET_PAD, frameHeight: 128 * 2 + 2 * CLOUD_SHEET_PAD, columns: 5, frames: 5 },
];

/**
 * One look a wind sprite takes: a sheet, a frame's size and the run of frames it plays.
 */
export interface WindVariant {
  readonly sheet: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly first: number;
  readonly frames: number;
}

/**
 * A group of four numbers, by wind strength: calm, breeze, windy, storm.
 */
export type ByStrength = readonly [calm: number, breeze: number, windy: number, storm: number];

/**
 * A wisp or dash preset: one sprite per instance, `count` instances staggered across `periodMs`, each living for its
 * variant's frames at `frameMs` each and drifting along the heading at `driftPxPerS`.
 *
 * `flipWithHeading` names the heading for which the sheet is flipped: the packs draw these for a wind blowing left, so it is
 * `"right"`.
 */
export interface WindSpritePreset {
  readonly id: string;
  readonly name: string;
  readonly group: "weather";
  readonly words: readonly string[];
  readonly kind: "wind-sprites";
  readonly domain: number;
  readonly variants: readonly WindVariant[];
  readonly count: ByStrength;
  readonly frameMs: ByStrength;
  readonly driftPxPerS: ByStrength;
  readonly opacity: ByStrength;
  readonly periodMs: number;
  readonly lane: readonly [number, number];
  readonly flipWithHeading: WindHeading;
}

/**
 * The cloud shadow preset: the clouds of a turn are a field that repeats every `tile` world pixels, drifting along the heading
 * at `speedPxPerS` times a hashed share in `speedSpread`, drawn at one of the `scales`.
 */
export interface CloudShadowPreset {
  readonly id: string;
  readonly name: string;
  readonly group: "weather";
  readonly words: readonly string[];
  readonly kind: "cloud-shadow";
  readonly domain: number;
  readonly sheet: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly shapes: number;
  readonly scales: readonly number[];
  readonly speedPxPerS: ByStrength;
  readonly speedSpread: readonly [number, number];
  readonly tile: { readonly width: number; readonly height: number };
  readonly fairCount: number;
  readonly cloudyCount: number;
  readonly fadeMs: number;
  readonly opacity: number;
  readonly tint: number;
}

const WISP_DOMAIN = 0x77697370;
const DASH_DOMAIN = 0x64617368;
const CLOUD_DOMAIN = 0x636c6f75;
const WISP_FRAMES = 48;
const WISP_FRAME_MS = 200;
const DASH_FRAME_MS = 80;
const WISP_DRIFT_PX_PER_S = 10;
const CLEAR = 0;

/**
 * The wisps: thin wavy lines that draw themselves across the screen (Sea Adventures' wind sheet, with the Village's longer
 * strokes as a second variant). One breeze wisp at a time, about 2 to 3 in wind and 4 to 5 in a storm; a wisp's frames are held
 * longer in a light wind (200, 160 and 100 ms, so a wisp lives 9.6, 7.7 and 4.8 s) and it drifts 10, 20 and 45 world pixels a second.
 */
export const WIND_WISP_PRESET: WindSpritePreset = {
  id: "wind-wisp",
  name: "Wisps",
  group: "weather",
  words: ["wind", "wisp", "breeze", "weather"],
  kind: "wind-sprites",
  domain: WISP_DOMAIN,
  variants: [
    { sheet: "wisp-sea", frameWidth: 288, frameHeight: 64, first: 0, frames: WISP_FRAMES },
    { sheet: "wisp-village", frameWidth: 312, frameHeight: 64, first: 0, frames: WISP_FRAMES },
  ],
  count: [0, 1, 4, 11],
  frameMs: [CLEAR, WISP_FRAME_MS, 160, 100],
  driftPxPerS: [CLEAR, WISP_DRIFT_PX_PER_S, 20, 45],
  opacity: [CLEAR, WISP_OPACITY.breeze, WISP_OPACITY.windy, WISP_OPACITY.storm],
  periodMs: WIND_PERIOD_MS,
  lane: WIND_LANE,
  flipWithHeading: "right",
};

/**
 * The gust dashes: short bright streaks, the four Highlands strips (the fourth's stray first frame dropped), in a wind and a
 * storm only.
 */
export const WIND_DASH_PRESET: WindSpritePreset = {
  id: "wind-dash",
  name: "Gusts",
  group: "weather",
  words: ["wind", "gust", "dash", "weather"],
  kind: "wind-sprites",
  domain: DASH_DOMAIN,
  variants: [
    { sheet: "dash-1", frameWidth: 224, frameHeight: 96, first: 0, frames: 14 },
    { sheet: "dash-2", frameWidth: 224, frameHeight: 96, first: 0, frames: 15 },
    { sheet: "dash-3", frameWidth: 224, frameHeight: 96, first: 0, frames: 10 },
    { sheet: "dash-4", frameWidth: 224, frameHeight: 96, first: 1, frames: 15 },
  ],
  count: [0, 0, 3, 6],
  frameMs: [CLEAR, DASH_FRAME_MS, DASH_FRAME_MS, DASH_FRAME_MS],
  driftPxPerS: [CLEAR, WISP_DRIFT_PX_PER_S, 20, 45],
  opacity: [CLEAR, DASH_OPACITY.breeze, DASH_OPACITY.windy, DASH_OPACITY.storm],
  periodMs: WIND_PERIOD_MS,
  lane: WIND_LANE,
  flipWithHeading: "right",
};

/**
 * The cloud shadows (Grass Land 1.6's five silhouettes, scaled and softened): a soft dark patch that crosses the ground at
 * 1.5 to 3 times the wisps' drift.
 */
export const CLOUD_SHADOW_PRESET: CloudShadowPreset = {
  id: "cloud-shadow",
  name: "Clouds",
  group: "weather",
  words: ["cloud", "shadow", "wind", "weather"],
  kind: "cloud-shadow",
  domain: CLOUD_DOMAIN,
  sheet: "cloud-shadows",
  frameWidth: 224 * 2 + 2 * CLOUD_SHEET_PAD,
  frameHeight: 128 * 2 + 2 * CLOUD_SHEET_PAD,
  shapes: 5,
  scales: [1, 1.25],
  speedPxPerS: [30, 44, 60, 110],
  speedSpread: [0.9, 1.2],
  tile: CLOUD_TILE,
  fairCount: FAIR_CLOUDS,
  cloudyCount: CLOUDY_CLOUDS,
  fadeMs: CLOUD_FADE_MS,
  opacity: CLOUD_SHADOW_OPACITY,
  tint: CLOUD_SHADOW_TINT,
};

/**
 * The wind presets, by id.
 */
export const WIND_PRESETS: Readonly<Record<string, WindSpritePreset | CloudShadowPreset>> = Object.freeze({ [WIND_WISP_PRESET.id]: WIND_WISP_PRESET, [WIND_DASH_PRESET.id]: WIND_DASH_PRESET, [CLOUD_SHADOW_PRESET.id]: CLOUD_SHADOW_PRESET });

/**
 * How many clouds a tile holds in a turn: none in rain or a storm (the dim already covers it), the fair-weather few in a
 * clear turn, rising with the front's wetness to the cloudier number.
 *
 * @param preset The cloud preset.
 * @param kind The turn's weather.
 * @param wetness The front's wetness, 0 to 255 (`Weather.wetness`).
 * @returns A whole number from 0 to `cloudyCount`.
 */
export function cloudCountFor(preset: CloudShadowPreset, kind: WeatherKind, wetness: number): number {
  if (kind === "rain" || kind === STORM_KIND) return 0;
  const share = wetness <= 0 ? 0 : wetness >= 255 ? 1 : wetness / 255;
  return preset.fairCount + Math.floor(share * (preset.cloudyCount - preset.fairCount));
}

/**
 * The strength a ByStrength group is read at.
 *
 * @param group The four numbers.
 * @param strength The wind's strength.
 * @returns The number for that strength.
 */
export function byStrength(group: ByStrength, strength: WindStrength): number {
  return group[strength];
}
